import { SlidersHorizontal, Tags } from 'lucide-react';
import { staleTimes, useMutation, useQuery } from '@platform/api-client';
import { Button, PageHeader, Tabs } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import {
  INVENTORY_TABS,
  type InventoryTab,
  type MovementState,
  type RecordMovementBody,
} from './inventory.ts';
import type { LabelSelection } from '../labels/labels.ts';
import { MovementSheet } from './MovementSheet.tsx';
import { useActiveLocations } from './useStockMatrix.ts';
import { useStockOverview } from '../dashboard/stock.ts';
import { InventoryKpis } from './InventoryKpis.tsx';
import { InventoryLevels } from './InventoryLevels.tsx';
import { InventoryMovements } from './InventoryMovements.tsx';
import { useMovementCounts } from './useMovementCounts.ts';
import styles from './Inventory.module.css';
import { DepotsTab } from './DepotsTab.tsx';
import { queryKeys, usePageContext } from '../usePageContext.ts';

export type InventoryPageProps = {
  api: TypedClient;
  tab: InventoryTab;
  state: MovementState | null;
  depot: string | null;
  onTabChange: (tab: InventoryTab) => void;
  onStateChange: (state: MovementState | null) => void;
  onDepotChange: (depot: string | null) => void;
  onPrintLabels: (selection: LabelSelection) => void;
  onShowDepotMovements: (depotId: string) => void;
};

export const InventoryPage = ({
  api,
  tab,
  onTabChange,
  onPrintLabels,
  onShowDepotMovements,
  ...movementFilters
}: InventoryPageProps) => {
  const { t, locale, tenantId, notify, describeError, invalidate } = usePageContext();
  const [sheetOpen, setSheetOpen] = useState(false);

  const locations = useActiveLocations(api);
  const archivedLocations = useQuery({
    queryKey: queryKeys.locations(tenantId, { status: 'archived' }),
    queryFn: () => api.get('/admin/locations', { query: { status: 'archived' } }),
    staleTime: staleTimes.reference,
  });
  const allLocations = [...(locations.data?.items ?? []), ...(archivedLocations.data?.items ?? [])];
  const overview = useStockOverview(api, null).data;
  const movementTotal = useMovementCounts(api, { depot: null, productId: null }).get('all');

  const record = useMutation({
    mutationFn: (body: RecordMovementBody) => api.post('/admin/stock-movements', { body }),
    onSuccess: async () => {
      setSheetOpen(false);
      notify(t('inventory.movementRecorded'));
      await invalidate('movements', 'low-stock', 'dashboard');
    },
  });

  const tabs = [
    {
      id: 'levels',
      label: t('inventory.levelsTab'),
      count: overview?.totals.variants,
      content: (
        <InventoryLevels api={api} locations={locations.data?.items ?? []} onPrintLabels={onPrintLabels} />
      ),
    },
    {
      id: 'movements',
      label: t('inventory.movementsTab'),
      count: movementTotal,
      content: (
        <InventoryMovements
          api={api}
          locations={allLocations}
          productId={null}
          onPrintLabels={onPrintLabels}
          {...movementFilters}
        />
      ),
    },
    {
      id: 'depots',
      label: t('inventory.depotsTab'),
      count: locations.data === undefined ? undefined : allLocations.length,
      content: (
        <DepotsTab
          api={api}
          query={locations}
          depots={allLocations}
          onShowMovements={(depot) => {
            onShowDepotMovements(depot.id);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('nav.inventory')}
        context={t('inventory.context')}
        action={
          <div className={styles['headerActions']}>
            <Button
              variant="outline"
              icon={<Tags size={20} aria-hidden="true" />}
              onClick={() => {
                onPrintLabels({ productIds: [], focus: null });
              }}
            >
              {t('labels.printLabels')}
            </Button>
            <Button
              icon={<SlidersHorizontal size={20} aria-hidden="true" />}
              onClick={() => {
                setSheetOpen(true);
              }}
            >
              {t('adjustStock.title')}
            </Button>
          </div>
        }
      />
      <div className={styles['overview']}>
        <InventoryKpis api={api} />
      </div>
      <Tabs
        framed
        locale={locale}
        tabs={tabs}
        activeId={tab}
        onChange={(id) => {
          onTabChange(INVENTORY_TABS.find((candidate) => candidate === id) ?? 'levels');
        }}
      />
      {sheetOpen ? (
        <MovementSheet
          open={sheetOpen}
          api={api}
          locations={locations.data?.items ?? []}
          submitting={record.isPending}
          errorMessage={record.isError ? (describeError(record.error) ?? null) : null}
          onClose={() => {
            setSheetOpen(false);
            record.reset();
          }}
          onSubmit={(body) => {
            record.mutate(body);
          }}
        />
      ) : null}
    </>
  );
};
